import { useEffect, useState } from 'react';
import { useBusinessId } from '@/hooks/use-tenant-params';
import { businessApi } from '@/lib/api-paths';
import { businessRoutes } from '@/lib/route-map';
import { Link, useNavigate } from 'react-router-dom';
import { Copy, Loader2, Pencil, Trash2 } from 'lucide-react';
import { PanelShell } from '@/components/panel-shell';
import { EmptyState } from '@/components/empty-state';
import { PageContainer } from '@/components/page-container';
import { PageError } from '@/components/page-error';
import { PageHeader } from '@/components/page-header';
import { Skeleton } from '@/components/skeleton';
import { Badge } from '@/components/ui/badge';
import { buttonVariants } from '@/components/ui/button';
import { invalidateMe, useMe } from '@/hooks/use-me';
import { api } from '@/lib/api';
import type { SurveySummary, SurveysListResponse, SurveyEditResponse } from '@/lib/types';
import { SurveyStatusBadge } from './ui';
import { fmtRelative } from './format';
import { NewSurveyButton } from './builder/new-survey-button';
import { surveyErrorCopy } from './builder/errors';

// =====================================================================
// SurveysList — /app/surveys. The org's surveys with response counts. Click a
// row to open its results; per-row Edit / Duplicate / Delete; "New survey"
// creates a draft and jumps into the builder (Phase 5). WP1 Task 15: swapped
// <DashboardShell> for <PanelShell> — this route is mounted org-level (no
// `:siteId` param), so PanelShell resolves the sidebar's active business via
// its own stored-business fallback (retired in T1.6B2; the URL is the tenant).
// =====================================================================

function SurveyRow({
  survey,
  onDuplicate,
  onDelete,
  busyId,
}: {
  survey: SurveySummary;
  onDuplicate: (id: string) => void;
  onDelete: (id: string) => void;
  busyId: string | null;
}) {
  // The Business half of the canonical route this list is rendered under.
  const businessId = useBusinessId() ?? '';
  const subtitle =
    survey.responseCount === 0
      ? 'No responses yet'
      : survey.lastResponseAt
        ? `Last response ${fmtRelative(survey.lastResponseAt)}`
        : `${survey.responseCount.toLocaleString()} responses`;
  const canDelete = survey.status === 'draft' && survey.responseCount === 0;
  const busy = busyId === survey.id;

  return (
    <li className="flex items-center gap-1 pr-2 transition-colors hover:bg-surface-sunken">
      <Link
        to={businessRoutes.survey(businessId, survey.id)}
        className="flex min-w-0 flex-1 items-center justify-between gap-4 px-5 py-4"
      >
        <div className="min-w-0">
          <div className="flex items-center gap-2.5">
            <span className="truncate font-medium text-foreground">{survey.title}</span>
            <SurveyStatusBadge status={survey.status} />
            {survey.heldCount > 0 ? (
              <Badge variant="warning" title="Held for review — see this survey's results for options">
                {survey.heldCount.toLocaleString()} held
              </Badge>
            ) : null}
          </div>
          <p className="mt-0.5 text-xs text-text-tertiary">{subtitle}</p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-semibold tabular-nums text-foreground">
            {survey.responseCount.toLocaleString()}
          </div>
          <div className="eyebrow">
            {survey.responseCount === 1 ? 'response' : 'responses'}
          </div>
        </div>
      </Link>
      <div className="flex shrink-0 items-center gap-0.5">
        <Link
          to={businessRoutes.surveyEdit(businessId, survey.id)}
          aria-label={`Edit ${survey.title}`}
          className="rounded-md p-2 text-text-secondary transition-colors hover:bg-card hover:text-text-primary"
        >
          <Pencil className="h-4 w-4" aria-hidden="true" />
        </Link>
        <button
          type="button"
          aria-label={`Duplicate ${survey.title}`}
          disabled={busy}
          onClick={() => onDuplicate(survey.id)}
          className="rounded-md p-2 text-text-secondary transition-colors hover:bg-card hover:text-text-primary disabled:opacity-40"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Copy className="h-4 w-4" aria-hidden="true" />}
        </button>
        {canDelete ? (
          <button
            type="button"
            aria-label={`Delete ${survey.title}`}
            disabled={busy}
            onClick={() => onDelete(survey.id)}
            className="rounded-md p-2 text-text-secondary transition-colors hover:bg-danger-bg hover:text-danger-fg disabled:opacity-40"
          >
            <Trash2 className="h-4 w-4" aria-hidden="true" />
          </button>
        ) : null}
      </div>
    </li>
  );
}

export function SurveysList() {
  // The Business API is addressed by the tenant the canonical route proved.
  // It is never derived from a Location id or a stored preference.
  const businessId = useBusinessId() ?? '';
  const meState = useMe();
  const navigate = useNavigate();
  const [surveys, setSurveys] = useState<SurveySummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    if (meState.kind !== 'ready') return;
    let cancelled = false;
    api
      .get<SurveysListResponse>(businessApi(businessId, 'surveys'))
      .then((res) => {
        if (!cancelled) setSurveys(res.surveys);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [businessId, meState.kind]);

  async function onDuplicate(id: string) {
    setBusyId(id);
    setError(null);
    try {
      const res = await api.post<SurveyEditResponse>(businessApi(businessId, `surveys/${id}/duplicate`));
      navigate(businessRoutes.surveyEdit(businessId, res.survey.id));
    } catch (e) {
      setError(e instanceof Error ? surveyErrorCopy(e.message) : 'Could not duplicate.');
      setBusyId(null);
    }
  }

  async function onDelete(id: string) {
    setBusyId(id);
    setError(null);
    try {
      await api.delete(businessApi(businessId, `surveys/${id}`));
      setSurveys((cur) => (cur ? cur.filter((s) => s.id !== id) : cur));
    } catch (e) {
      setError(e instanceof Error ? surveyErrorCopy(e.message) : 'Could not delete.');
    } finally {
      setBusyId(null);
    }
  }

  if (meState.kind === 'error') {
    // Keeps the page's own identity — same eyebrow and <h1> as the ready
    // state below — when /api/me fails. See components/page-error.tsx.
    return (
      <div className="px-6 py-8">
        {/* NOT wrapped in <PanelShell>: the shell reads the sidebar out of the
            very /api/me payload that just failed, and its own error branch
            returns BEFORE its children — mounting it here would replace this
            header with the shell's bare paragraph and lose exactly the page
            identity this exists to keep. The wrapper restores the gutter
            PanelShell's <main> would have supplied (PageContainer sets none). */}
        <PageError
          eyebrow="Pulse"
          title="Surveys"
          message={meState.message}
          retry={invalidateMe}
        />
      </div>
    );
  }
  if (meState.kind !== 'ready') return null;

  return (
    <PanelShell>
      <PageContainer>
        {/* Section is Pulse, not "Surveys": the sidebar lists Surveys as a
            Pulse child (lib/nav-model.ts), and an eyebrow that repeats its
            own h1 says nothing. "Surveys" IS the section for the per-survey
            screens below it (survey-analytics.tsx). */}
        <PageHeader
          eyebrow="Pulse"
          title="Surveys"
          description="Build a survey, collect responses, and read the results — all in one place."
          // Design audit T8 (A11, one primary per screen): with no surveys
          // yet, the EmptyState's own "New survey" is the screen's single
          // primary — the header copy of the same button doubled it. The
          // header button comes back the moment there is a list to sit above
          // (and stays put while the list is still loading, so the action
          // does not pop in after the fetch).
          actions={surveys !== null && surveys.length === 0 ? undefined : <NewSurveyButton />}
        />

        {error ? (
          <p role="alert" className="rounded-md bg-danger-bg px-3 py-2 text-sm text-danger-fg">
            {error}
          </p>
        ) : null}

        {surveys === null ? (
          <div className="flex flex-col gap-2">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-[4.5rem] w-full rounded-xl" />
            ))}
          </div>
        ) : surveys.length === 0 ? (
          <EmptyState
            title="No surveys yet"
            description="Create your first survey to start collecting responses."
            action={<NewSurveyButton />}
          />
        ) : (
          <ul className="divide-y divide-border-subtle overflow-hidden rounded-xl border border-border-subtle bg-card">
            {surveys.map((s) => (
              <SurveyRow
                key={s.id}
                survey={s}
                onDuplicate={onDuplicate}
                onDelete={onDelete}
                busyId={busyId}
              />
            ))}
          </ul>
        )}
      </PageContainer>
    </PanelShell>
  );
}

export default SurveysList;
